"use strict";
// The frozen payload is the sole stored authority for a document's face values (FR-6): the gross
// total the paper prints, each itemized advance deduction, and the amount due. amount_signed on the
// row holds only the net amount due (R19); a reader that wants the printed figures reads this
// payload, never amount_signed.
Object.defineProperty(exports, "__esModule", { value: true });
exports.FrozenPayloadInconsistentError = exports.frozenPayloadSchema = exports.frozenPayloadTermsSchema = exports.frozenPayloadPricingSchema = exports.frozenPayloadTradeSchema = exports.SUPPORTED_FROZEN_PAYLOAD_SCHEMA_VERSIONS = exports.FROZEN_PAYLOAD_SCHEMA_VERSION = void 0;
exports.assertPayloadConsistent = assertPayloadConsistent;
exports.buildFrozenPayload = buildFrozenPayload;
const zod_1 = require("zod");
const money_1 = require("./money");
const vocabulary_1 = require("./vocabulary");
exports.FROZEN_PAYLOAD_SCHEMA_VERSION = 2;
// Version 1 rows carry no trade, pricing or terms block. They stay readable forever: an issued
// document's bytes must reproduce at any later date, so a reader that refused the older shape
// would break every document issued before the vetted template landed.
exports.SUPPORTED_FROZEN_PAYLOAD_SCHEMA_VERSIONS = [1, 2];
const moneySchema = zod_1.z.string().refine(money_1.isMoney, (value) => ({
    message: `Not a canonical Money value: ${JSON.stringify(value)}`,
}));
const frozenPayloadAdvanceDeductionSchema = zod_1.z.object({
    advance_document_id: zod_1.z.string().uuid(),
    advance_document_number: zod_1.z.string().min(1),
    amount: moneySchema,
});
// A goods line. `amount` is stored, never derived at render time: quantity times unit price
// routinely lands beyond two decimals (87.451 MT at 8,845.00 is 773,504.095), and a document that
// derived it would either print a third decimal or fail to render. Storing it puts the rounding
// with the writer, where the remainder can be placed deliberately, and it makes the sum-to-gross
// rule below checkable exactly.
//
// The four pricing fields are per line because the vetted goods table gives each row its own Base
// Price, Payable Factor and P/D column. They were document-level while the table had one row.
const frozenPayloadLineSchema = zod_1.z.object({
    description: zod_1.z.string().min(1),
    quantity: zod_1.z.string().min(1),
    unit: zod_1.z.string().min(1),
    unit_price: zod_1.z.string().min(1),
    // Optional only so a version 1 payload written before goods lines carried figures still parses.
    // assertPayloadConsistent requires it on every version 2 line.
    amount: moneySchema.optional(),
    base_price: zod_1.z.string().min(1).nullable().optional(),
    payable_factor: zod_1.z.string().min(1).nullable().optional(),
    premium_discount: zod_1.z.string().min(1).nullable().optional(),
    weight_tolerance: zod_1.z.string().min(1).nullable().optional(),
});
// Every field below is a string the paper states, captured at append time and never recomputed.
// FR-2 bars generation from reading a bl_order, so a figure that is not frozen here can never
// reach a render. Each figure is stored already in its printed form: this module performs no
// arithmetic on any of them, which is what keeps the values a document prints identical to the
// values the trade carried on the day it was written.
const nullableText = zod_1.z.string().min(1).nullable();
exports.frozenPayloadTradeSchema = zod_1.z.object({
    bl_number: nullableText,
    bl_order_name: nullableText,
    container_count: nullableText,
    container_numbers: nullableText,
    vessel_voyage: nullableText,
    shipping_line: nullableText,
    transport_mode: nullableText,
    port_of_loading: nullableText,
    port_of_discharge: nullableText,
    final_destination: nullableText,
    eta: nullableText,
    incoterms: nullableText,
    packing: nullableText,
    country_of_origin: nullableText,
    description_of_goods: nullableText,
    isri_grade: nullableText,
    loaded_quantity_mt: nullableText,
    total_net_weight: nullableText,
});
// Per-unit figures carry their own precision, which is often finer than the two decimals a total
// prints. They stay plain decimal strings rather than Money for that reason, and the template
// prints them verbatim, exactly as it already prints a line's unit_price.
exports.frozenPayloadPricingSchema = zod_1.z.object({
    base_price: nullableText,
    premium_discount: nullableText,
    unit_price: nullableText,
    payable_factor: nullableText,
    lme_reference: nullableText,
    quotation_period_start: nullableText,
    quotation_period_end: nullableText,
    // Defaults null so a payload written before this key existed still parses (read-only default).
    quotation_period_rule: nullableText.default(null),
    weight_tolerance: nullableText,
    contract_term: nullableText,
    pricing_remarks: nullableText,
});
// service_date is the Leistungsdatum §14 UStG requires, and it is not the document date. due_date
// and payment_terms freeze here because invoice_ledger_document carries no column for either.
exports.frozenPayloadTermsSchema = zod_1.z.object({
    service_date: nullableText,
    due_date: nullableText,
    payment_terms: nullableText,
    payment_reference: nullableText,
});
exports.frozenPayloadSchema = zod_1.z.object({
    schema_version: zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2)]),
    currency: vocabulary_1.ledgerCurrencySchema,
    gross_total: moneySchema,
    advance_deductions: zod_1.z.array(frozenPayloadAdvanceDeductionSchema),
    amount_due: moneySchema,
    frozen_at: zod_1.z.string().datetime(),
    lines: zod_1.z.array(frozenPayloadLineSchema).optional(),
    trade: exports.frozenPayloadTradeSchema.optional(),
    pricing: exports.frozenPayloadPricingSchema.optional(),
    terms: exports.frozenPayloadTermsSchema.optional(),
});
class FrozenPayloadInconsistentError extends Error {
    constructor(reason) {
        super(`Frozen payload is inconsistent: ${reason}`);
        this.name = 'FrozenPayloadInconsistentError';
    }
}
exports.FrozenPayloadInconsistentError = FrozenPayloadInconsistentError;
// THE KEY RULE: a payload whose gross less its itemized deductions does not equal the amount due
// is unwritable. This is what stops the Quba double-deduct shape (gross 89,000 / prepaid 21,000 /
// due 68,000 on an invoice whose real due is 89,000) at write time, structurally, not by review.
function assertPayloadConsistent(payload) {
    const nonPositiveDeduction = payload.advance_deductions.find((deduction) => (0, money_1.isNegativeMoney)(deduction.amount) || (0, money_1.isZeroMoney)(deduction.amount));
    if (nonPositiveDeduction !== undefined) {
        throw new FrozenPayloadInconsistentError(`advance deduction ${nonPositiveDeduction.advance_document_id} must be positive, got ${nonPositiveDeduction.amount}`);
    }
    const deductionsTotal = (0, money_1.sumMoney)(payload.advance_deductions.map((deduction) => deduction.amount));
    const derivedDue = (0, money_1.subtractMoney)(payload.gross_total, deductionsTotal);
    if ((0, money_1.compareMoney)(derivedDue, payload.amount_due) !== 0) {
        throw new FrozenPayloadInconsistentError(`gross ${payload.gross_total} less deductions ${deductionsTotal} is ${derivedDue}, which ` +
            `disagrees with the stated amount due ${payload.amount_due}`);
    }
    assertLinesSumToGross(payload);
}
// THE SECOND KEY RULE, and the one multiple goods lines made necessary. Only goods lines belong on
// a goods invoice: other charges are billed separately (ruled 2026-08-11). So the lines a document
// prints must add up to the gross it prints. With a single line nobody could tell the difference,
// because the vetted table printed the gross in the line's own total column. With several lines a
// document could otherwise show rows that do not add up to its own subtotal.
//
// Version 1 payloads predate stored line amounts and are exempt: an issued document must still
// reproduce byte-identically at any later date.
function assertLinesSumToGross(payload) {
    if (payload.schema_version < 2)
        return;
    const lines = payload.lines;
    if (lines === undefined || lines.length === 0)
        return;
    const amounts = [];
    for (const line of lines) {
        if (line.amount === undefined) {
            throw new FrozenPayloadInconsistentError(`goods line "${line.description}" carries no amount, which every line must state`);
        }
        amounts.push(line.amount);
    }
    const linesTotal = (0, money_1.sumMoney)(amounts);
    if ((0, money_1.compareMoney)(linesTotal, payload.gross_total) !== 0) {
        throw new FrozenPayloadInconsistentError(`the goods lines total ${linesTotal}, which disagrees with the stated gross ` +
            payload.gross_total);
    }
}
function buildFrozenPayload(input) {
    const payload = {
        schema_version: exports.FROZEN_PAYLOAD_SCHEMA_VERSION,
        currency: input.currency,
        gross_total: input.grossTotal,
        advance_deductions: input.advanceDeductions.map((deduction) => ({
            advance_document_id: deduction.advanceDocumentId,
            advance_document_number: deduction.advanceDocumentNumber,
            amount: deduction.amount,
        })),
        amount_due: input.amountDue,
        frozen_at: input.frozenAt.toISOString(),
        lines: input.lines === undefined ? undefined : [...input.lines],
        trade: input.trade === undefined ? undefined : { ...input.trade },
        pricing: input.pricing === undefined ? undefined : { ...input.pricing },
        terms: input.terms === undefined ? undefined : { ...input.terms },
    };
    assertPayloadConsistent(payload);
    return payload;
}
//# sourceMappingURL=frozen-payload.js.map